import Map from 'ol/Map';
import View from 'ol/View';

import { Layer } from './layer';
import { Interaction } from './interaction/interaction';
import Control from 'ol/control/Control';

import { defaults } from 'ol/control';

import { mousePosition } from './control/mouse_position';
import { timePosition } from './control/time_position';
import { Legend } from './control/legend';
import { TimeSlider } from './control/time_slider';
import { LoadingIndicator } from './control/loading_indicator';
import { initPositionContainer, link_control } from './control/position';

import ScaleLine from 'ol/control/ScaleLine';

import { get as getProjection, transform } from 'ol/proj';
import { unByKey } from 'ol/Observable';

import { toScale, toResolution, MIN_SCALE } from './scale_util';

import './proj4_defs.js';
import 'ol/ol.css';
import './map.css';

// reads the map size with a 50 ms interval
// returning a default if the value is not yet
// available after 5 attemps
const getSizePromise = async (map) => {
   let attempts = 0;
   let maxAttemps = 5; 
   
   return new Promise((resolve, reject) => {
    const timer = setInterval(() => {
            let size = map.getSize();

            if( (size === undefined || size[0] === 0) && attempts >= maxAttemps ) {
                size = [100, 100];
            }

            if( (size && size[0] > 0) )  {
                resolve(size);
                clearTimeout(timer);
            }

            attempts++; 
        }, 50);
    }); 
}

class MapControlComponent {
	constructor(options) {
		const { element, scale, center } = options;
		
		const projection = getProjection(`EPSG:${options.projection}`);
		const displayProjection = getProjection(`EPSG:${options.displayProjection}`);

		this.defaultScale = scale;
		this.defaultCenter = transform(center, displayProjection, projection);		
		
		const view = new View({
			projection: projection,
			maxZoom: 23 // 1-m +/-
		});
		
		const scaleLineControl = new ScaleLine();

        const map = new Map({
            layers: [],
            target: element,
            view: view,
            controls : defaults({
                attributionOptions: {
                    collapsible: false
                }
            }).extend([scaleLineControl, new Legend(), new TimeSlider()])
        });

        this.view = view;
        this.map = map;
        
        // create container
        const positionContainer = initPositionContainer(map);
        const mousePositionControl = mousePosition(displayProjection, positionContainer);
        const timePositionControl = timePosition(positionContainer);
        const loadingIndicatorControl = new LoadingIndicator({
        	target: positionContainer
        });
        
        map.addControl( mousePositionControl );
        map.addControl( timePositionControl );
        map.addControl( loadingIndicatorControl );
                
        link_control(map.getViewport(), scaleLineControl, positionContainer);
	}	
	
	getCenter() {
		return this.view.getCenter();
	}
	
	setCenter(center) {
		this.view.setCenter(center);
	}
	
	getResolution() {
		return this.view.getResolution();
	}
	
	getScale() {
		return this.toScale(this.getResolution());
	} 	
	
	setScale(scale) {
		this.view.setResolution(this.toResolution(scale));
	}
	
	getExtent() {
		return this.view.calculateExtent(this.map.getSize());
	}
	
	setExtent(extent) {
		let valid = extent && true;
		
		if( valid ) {		
			for (var i = 0, l = extent.length; i < l && valid; i++) {
				valid = valid && isFinite(extent[i]);
			}
		}			
		
		if( valid ) {
            this.view.fit(extent, {
                minResolution: this.toResolution(MIN_SCALE)
            });
		} else if( this.defaultCenter && this.defaultScale ) {
		    this.setCenter( this.defaultCenter );
		    this.setScale( this.defaultScale );
		}
		
	}
	
	getSize() {
		return this.map.getSize();
	}
	
	fit(geometry) {
		let geometryOrExtent = geometry;
		if( geometry && geometry.getType() === 'GeometryCollection' ) {
			geometryOrExtent = geometry.getExtent();
		}
    
	    // size might not be available yet
	    getSizePromise(this.map)
	    .then(size => {
            
            this.view.fit(geometryOrExtent, {
                constrainResolution: true,
                minResolution: this.toResolution(MIN_SCALE),
                size: size,
                padding: [10, 10, 10, 10]
            }); 
        })
	}
	
	addLayer(layer) {
		if( layer instanceof Layer ) {
			this.map.addLayer(layer.layer);
		} else {
			console.warn(layer);
		}
	}
	
	removeLayer(layer) {	
		if( layer instanceof Layer ) {
			this.map.removeLayer(layer.layer);
		} else {
			console.warn(layer);
		}
	}
	
	addInteraction(interaction) {
		if( interaction instanceof Interaction ) {
			this.map.addInteraction(interaction.interaction);
			interaction.setActive(true);
		} else {
			console.warn(interaction);
		}
	}
	
	removeInteraction(interaction) {
		if( interaction instanceof Interaction ) {
			interaction.setActive(false);
			this.map.removeInteraction(interaction.interaction);
		} else {
			console.warn(interaction);
		}
	}
	
	addControl(control) {
		if( control instanceof Control ) {
			this.map.addControl(control);
		} else {
			console.warn(control);
		}
	}
	
	removeControl(control) {
		if( control instanceof Control ) {
			this.map.removeControl(control);
		} else {
			console.warn(control);
		}
	}
	
	getProjection() {
		const projection = this.view.getProjection();
		return projection;
	}
	
	updateSize() {
		this.map.updateSize();
	}
	
	addExtentChangeHandler(handler) {
		const key = this.map.on('moveend', ({coordinate}) => handler(coordinate));
		return () => unByKey(key);	
	}
	
	addMapClickHandler(handler) {
		const key = this.map.on('singleclick', ({coordinate}) => handler(coordinate));
		return () => unByKey(key);
	}
	
	toScale(resolution) {
		return toScale(this.view.getProjection(), resolution);
	}
	
	toResolution(scale) {
	    return toResolution(this.view.getProjection(), scale);
	}
}

const MapControl = (options) => {
	return new MapControlComponent(options);
};

export {
	MapControl, MIN_SCALE
}